import {
  DocumentNode,
  gql,
  LazyQueryHookOptions,
  OperationVariables,
  TypedDocumentNode,
  useLazyQuery,
  useMutation,
} from '@apollo/client'
import { useForm } from 'antd/lib/form/Form'
import { useCallback, useEffect } from 'react'
import { client } from '.'
import { useAuth } from '../lib/hooks'
import { D4_Demande, D4_Demande_Set_Input, Logactivite_Insert_Input, Query_Root, SmithOutput } from '../types'
import { DEMANDE_UPDATE } from './mutation/demande.mutation'
import { LOG_MUTATION } from './mutation/log.mutation'
import { parseGqlError } from './utils'
/**
 * Connecte un formulaire avec un resolver
 * pour pouvoir valider le formulaire async
 * et recuperer les erreurs
 * @param query gql
 * @param options apollo options
 */
export function useGqlConnectedForm<TData = any, TVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: LazyQueryHookOptions<TData, TVariables>
) {
  const [executeQuery, { loading, data, error }] = useLazyQuery<TData, TVariables>(query, options)

  const [form] = useForm()

  useEffect(() => {
    if (!error) {
      return
    }
    const errors: { [k: string]: string } = error.graphQLErrors.reduce(parseGqlError, {})

    Object.keys(errors).forEach((field: string) => {
      const err: string = errors[field]
      form.setFields([
        {
          name: field,
          errors: [err],
        },
      ])
    })
  }, [error, form])

  return [form, executeQuery, { loading, data, error }] as const
}

const SMITH_QUERY = gql`
  query Smith($text: String!) {
    smith(text: $text) {
      prediction
    }
  }
`
export const useSmith = () => {
  const fn = useLazyQuery<{ smith: SmithOutput }, { text: string }>(SMITH_QUERY)

  return fn
}

type QueriesTypenames = keyof Omit<Query_Root, '__typename'>

export const useLog = () => {
  const {
    user: {
      personne: { id },
    },
  } = useAuth()

  const [log] = useMutation<{ id: number }, { data: Logactivite_Insert_Input }>(LOG_MUTATION)

  const fn = useCallback(
    (params) => {
      const {
        variables: { data },
      } = params
      log({ ...params, variables: { data: { user_id: id, ...data } } })
    },
    [id, log]
  )

  return fn
}
/**
 * Permet de recupere une entite dans le cache apollo, mais
 * cette fonction n'est pas optimum
 * @param param0 {typename de l'entite, id, champs voulus}
 * @returns une entité dans le cache apollo
 */
export const queryCache = <T>({
  typename,
  id,
  fields,
}: {
  typename: QueriesTypenames
  id: string | number
  fields: [keyof T]
}): T => {
  const res = client.readFragment({
    id: typename + ':' + id,
    fragment: gql`
        fragment Q on ${typename} {
          id
          ${fields.join('\n')}
        }
      `,
  })
  return res
}

// declare function useGqlConnectedForm<TData = any, TVariables = OperationVariables>(query: DocumentNode | TypedDocumentNode<TData, TVariables>, options?: LazyQueryHookOptions<TData, TVariables>): QueryTuple<TData, TVariables>;

export const useDemandeUpdate = () => {
  const mut = useMutation<{ demande: D4_Demande }, { id: number; data: D4_Demande_Set_Input }>(DEMANDE_UPDATE)
  return mut
}
